// draw.io (mxGraph mxfile) export & import. EVERY page is exported as fully
// editable, native mxCells: device pages (icons + edges), detail pages
// (device boxes + port rows + connections), the logical diagram and the VLAN
// list (native draw.io tables). A CAD frame + title block (grouped under a
// netappFrame container) is drawn natively on every page. Unmodified SwitchR
// exports still re-import cleanly (the frame group + generated pages are skipped
// and the live detail pages are rebuilt from the hidden meta cell).
import { iconDataUri, deviceLabel } from './deviceIcons';
import { connDrawioStyle, computeSideBundles } from './connections';
import { SHEET } from './svgExport';
import { frameCells, detailCells, locationCells, cableListCells, vlanListCells, usedConnTypes, makeIdGen } from './drawioNative';
import { displayEdgesForPage } from './detail';
import { safeImageHref, sanitizeDocImages } from './safeUri';
import { pageDisplayName } from './pageNames';

const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'id-' + Math.random().toString(36).slice(2, 10);

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function defaultTitleBlock() {
  return {
    projekt: '',
    titel: '',
    zeichnungsnummer: 'NET-001',
    datum: new Date().toISOString().slice(0, 10),
    ersteller: '',
    geprueft: '',
    revision: 'A',
    firma: '',
    logo: '',
  };
}

function nodeCell(node, doc, lang, labelPos, scale = 1) {
  const s = scale || 1;
  // Alias placement (a clone/view of a master device that lives on another
  // standard page). Export a VISUAL cell only (icon + label) tagged with
  // netappAlias/netappAliasOf, WITHOUT the full device payload. On import it is
  // rebuilt as an alias placement; the master remains the single source of truth.
  if (node.data?.aliasOf) {
    let master = null;
    for (const p of doc.pages || []) {
      const m = (p.nodes || []).find((x) => x.id === node.data.aliasOf && !x.data?.aliasOf);
      if (m) {
        master = m;
        break;
      }
    }
    const md = master?.data || {};
    const vlanA = doc.vlans.find((v) => v.id === md.vlanRef);
    const ipA = md.ip || '';
    const nameA = md.label || deviceLabel(md.deviceType, lang);
    const typeTextA = deviceLabel(md.deviceType, lang);
    const showTypeA = nameA !== typeTextA;
    const subA = [vlanA ? 'VLAN' + vlanA.vlanId : '', ipA].filter(Boolean).join(' \u00b7 ');
    const labelInnerA =
      (showTypeA ? `<span style='font-size:9px;color:#7A8591'>${esc(typeTextA)}</span><br>` : '') +
      esc(nameA) +
      (subA ? `<br><span style='font-size:9px;color:#5B6672'>${esc(subA)}</span>` : '');
    const labelHtmlA = esc(labelInnerA);
    const posA =
      labelPos === 'left'
        ? 'verticalLabelPosition=middle;verticalAlign=middle;labelPosition=left;align=right;spacingRight=6;'
        : 'verticalLabelPosition=bottom;verticalAlign=top;align=center;';
    const styleA = `shape=image;html=1;whiteSpace=wrap;${posA}labelBackgroundColor=none;imageAspect=0;aspect=fixed;image=${iconDataUri(md.deviceType)};fontSize=${Math.round(12 * s)};fontColor=#111418;`;
    const xA = Math.round((node.position?.x || 0) * s);
    const yA = Math.round((node.position?.y || 0) * s);
    const whA = Math.round(64 * s);
    return (
      `<object label="${labelHtmlA}" id="${esc(node.id)}" netappAlias="1" netappAliasOf="${esc(node.data.aliasOf)}" deviceType="${esc(md.deviceType || '')}">` +
      `<mxCell style="${styleA}" vertex="1" parent="1"><mxGeometry x="${xA}" y="${yA}" width="${whA}" height="${whA}" as="geometry"/></mxCell>` +
      `</object>`
    );
  }
  const vlan = doc.vlans.find((v) => v.id === node.data?.vlanRef);
  const ip = node.data?.ip || '';
  const name = node.data?.label || deviceLabel(node.data?.deviceType, lang);
  const typeText = deviceLabel(node.data?.deviceType, lang);
  const showType = name !== typeText;
  const sub = [vlan ? 'VLAN' + vlan.vlanId : '', ip].filter(Boolean).join(' \u00b7 ');
  const labelInner =
    (showType ? `<span style='font-size:9px;color:#7A8591'>${esc(typeText)}</span><br>` : '') +
    esc(name) +
    (sub ? `<br><span style='font-size:9px;color:#5B6672'>${esc(sub)}</span>` : '');
  const labelHtml = esc(labelInner);
  const pos =
    labelPos === 'left'
      ? 'verticalLabelPosition=middle;verticalAlign=middle;labelPosition=left;align=right;spacingRight=6;'
      : 'verticalLabelPosition=bottom;verticalAlign=top;align=center;';
  const style = `shape=image;html=1;whiteSpace=wrap;${pos}labelBackgroundColor=none;imageAspect=0;aspect=fixed;image=${iconDataUri(node.data?.deviceType)};fontSize=${Math.round(12 * s)};fontColor=#111418;`;
  const x = Math.round((node.position?.x || 0) * s);
  const y = Math.round((node.position?.y || 0) * s);
  const wh = Math.round(64 * s);
  const portsJson = esc(JSON.stringify(node.data?.ports || []));
  const panelJson = esc(JSON.stringify(node.data?.panel || { groups: [] }));
  const portGroupsJson = esc(JSON.stringify(node.data?.portGroups || []));
  const interfacesJson = esc(JSON.stringify(node.data?.interfaces || []));
  return (
    `<object label="${labelHtml}" id="${esc(node.id)}" netappLabel="${esc(name)}" deviceType="${esc(node.data?.deviceType || '')}" netappNode="1" netappVlanRef="${esc(node.data?.vlanRef || '')}" vlanId="${vlan ? vlan.vlanId : ''}" vlanName="${esc(vlan ? vlan.name : '')}" deviceIp="${esc(ip)}" netappDescription="${esc(node.data?.description || '')}" netappPorts="${portsJson}" netappPanel="${panelJson}" netappPortGroups="${portGroupsJson}" netappInterfaces="${interfacesJson}">` +
    `<mxCell style="${style}" vertex="1" parent="1"><mxGeometry x="${x}" y="${y}" width="${wh}" height="${wh}" as="geometry"/></mxCell>` +
    `</object>`
  );
}

function portNameOf(node, portId) {
  if (!node || !portId) return '';
  const p = (node.data?.ports || []).find((x) => x.id === portId);
  return p ? p.name : '';
}

function sideExit(side) {
  if (side === 'l') return 'exitX=0;exitY=0.5;exitDx=0;exitDy=0;';
  if (side === 'r') return 'exitX=1;exitY=0.5;exitDx=0;exitDy=0;';
  if (side === 't') return 'exitX=0.5;exitY=0;exitDx=0;exitDy=0;';
  if (side === 'b') return 'exitX=0.5;exitY=1;exitDx=0;exitDy=0;';
  return '';
}
function sideEntry(side) {
  if (side === 'l') return 'entryX=0;entryY=0.5;entryDx=0;entryDy=0;';
  if (side === 'r') return 'entryX=1;entryY=0.5;entryDx=0;entryDy=0;';
  if (side === 't') return 'entryX=0.5;entryY=0;entryDx=0;entryDy=0;';
  if (side === 'b') return 'entryX=0.5;entryY=1;entryDx=0;entryDy=0;';
  return '';
}

function edgeCell(edge, nodeIndex, lagNameById = {}, mirrored = false, pageId = '', bundle = {}) {
  const id = mirrored ? esc(`${edge.id}__m_${pageId}`) : esc(edge.id);
  const op = mirrored ? 'strokeOpacity=40;opacity=40;' : '';
  const style = `edgeStyle=orthogonalEdgeStyle;rounded=0;html=1;endArrow=none;${sideExit(edge.sourceSide)}${sideEntry(edge.targetSide)}${connDrawioStyle(edge.connType)}${op}`;
  const sName = portNameOf(nodeIndex[edge.source], edge.sourcePortId);
  const tName = portNameOf(nodeIndex[edge.target], edge.targetPortId);
  const lagName = edge.lagId ? lagNameById[edge.lagId] || '' : '';
  const cname = esc(edge.name || '');
  // A MIRROR cell is a display-only copy of a connection whose home page is a
  // different standard page. It is tagged netappMirror so the importer ignores
  // it (the real connection is imported once, from its home page).
  const attrs = mirrored
    ? `netappMirror="1" netappEdgeRef="${esc(edge.id)}" netappConnType="${esc(edge.connType || 'ethernet')}"`
    : `netappEdge="1" netappConnType="${esc(edge.connType || 'ethernet')}" netappName="${cname}" netappSourcePortId="${esc(edge.sourcePortId || '')}" netappTargetPortId="${esc(edge.targetPortId || '')}" netappSourceSide="${esc(edge.sourceSide || '')}" netappTargetSide="${esc(edge.targetSide || '')}" netappLagId="${esc(edge.lagId || '')}" netappLagName="${esc(lagName)}"`;
  let out =
    `<object id="${id}" ${attrs} label="${cname}">` +
    `<mxCell style="${style}" edge="1" parent="1" source="${esc(edge.source)}" target="${esc(edge.target)}"><mxGeometry relative="1" as="geometry"/></mxCell>` +
    `</object>`;
  // Bundled device sides (>3 cables) converge on one anchor: replace the many
  // per-cable port labels with a single accent count label at that end.
  const countStyle = 'edgeLabel;resizable=0;html=1;rounded=1;fillColor=#ffffff;strokeColor=#0B5FFF;fontSize=10;fontStyle=1;fontColor=#0B5FFF;spacing=2;';
  if (bundle.sourceLabelOwner)
    out += `<mxCell id="${id}-sc" value="${esc(String(bundle.sourceCount))}" style="${countStyle}${op}" connectable="0" vertex="1" parent="${id}"><mxGeometry x="-0.7" relative="1" as="geometry"><mxPoint x="0" y="0" as="offset"/></mxGeometry></mxCell>`;
  else if (sName && !bundle.sourceCollapsed)
    out += `<mxCell id="${id}-sp" value="${esc(sName)}" style="edgeLabel;resizable=0;html=1;align=left;verticalAlign=bottom;fontSize=9;fontColor=#2A3138;${op}" connectable="0" vertex="1" parent="${id}"><mxGeometry x="-0.7" relative="1" as="geometry"><mxPoint x="0" y="0" as="offset"/></mxGeometry></mxCell>`;
  if (bundle.targetLabelOwner)
    out += `<mxCell id="${id}-tc" value="${esc(String(bundle.targetCount))}" style="${countStyle}${op}" connectable="0" vertex="1" parent="${id}"><mxGeometry x="0.7" relative="1" as="geometry"><mxPoint x="0" y="0" as="offset"/></mxGeometry></mxCell>`;
  else if (tName && !bundle.targetCollapsed)
    out += `<mxCell id="${id}-tp" value="${esc(tName)}" style="edgeLabel;resizable=0;html=1;align=right;verticalAlign=bottom;fontSize=9;fontColor=#2A3138;${op}" connectable="0" vertex="1" parent="${id}"><mxGeometry x="0.7" relative="1" as="geometry"><mxPoint x="0" y="0" as="offset"/></mxGeometry></mxCell>`;
  return out;
}

function metaCell(doc) {
  const vlansJson = esc(JSON.stringify(doc.vlans || []));
  const tbJson = esc(JSON.stringify(doc.titleBlock || {}));
  const detailPages = (doc.pages || [])
    .filter((p) => p.type === 'detail')
    .map((p) => ({ id: p.id, name: p.name, members: p.members || [], showStubs: !!p.showStubs, viewMode: p.viewMode === 'panel' ? 'panel' : 'list', contentScale: p.contentScale || 1 }));
  const detailJson = esc(JSON.stringify(detailPages));
  const locationPages = (doc.pages || [])
    .filter((p) => p.type === 'location')
    .map((p) => ({ id: p.id, name: p.name, markers: p.markers || [], background: p.background || { kind: 'none', dataUri: '' }, showConnections: p.showConnections !== false }));
  const locationJson = esc(JSON.stringify(locationPages));
  const orderJson = esc(JSON.stringify((doc.pages || []).map((p) => p.id)));
  const labelPosMap = {};
  (doc.pages || []).forEach((p) => {
    if (p.type !== 'detail') labelPosMap[p.id] = p.labelPos === 'left' ? 'left' : 'below';
  });
  const labelPosJson = esc(JSON.stringify(labelPosMap));
  const pageScaleMap = {};
  (doc.pages || []).forEach((p) => {
    pageScaleMap[p.id] = p.contentScale || 1;
  });
  const pageScaleJson = esc(JSON.stringify(pageScaleMap));
  const zonesJson = esc(JSON.stringify(doc.zones || []));
  const zonesEnabled = doc.zonesEnabled ? '1' : '0';
  return (
    `<object id="netapp-meta" netappMeta="1" netappVlans="${vlansJson}" netappTitleBlock="${tbJson}" netappName="${esc(doc.name || '')}" netappDetailPages="${detailJson}" netappLocationPages="${locationJson}" netappPageOrder="${orderJson}" netappLabelPos="${labelPosJson}" netappPageScale="${pageScaleJson}" netappZonesEnabled="${zonesEnabled}" netappZones="${zonesJson}" label="">` +
    `<mxCell style="text;html=1;" vertex="1" parent="1" visible="0"><mxGeometry x="-2000" y="-2000" width="10" height="10" as="geometry"/></mxCell>` +
    `</object>`
  );
}

function modelWrap(cellsXml) {
  return (
    `<mxGraphModel dx="1100" dy="800" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="${SHEET.w}" pageHeight="${SHEET.h}" math="0" shadow="0">` +
    `<root><mxCell id="0"/><mxCell id="1" parent="0"/>${cellsXml}</root></mxGraphModel>`
  );
}

// A visible "do not edit in draw.io" banner placed just above each sheet. It is
// only meant to be seen inside draw.io — SwitchR ignores it on import via the
// netappNote marker (so it never counts as a dropped shape). Sits above the page
// (negative y) so it does not overlap the drawing or the printed PDF/SVG.
const FRAME_OUTER = 26;
function noteCell(lang, idgen) {
  const id = idgen ? idgen('note') : 'netapp-note';
  const msg =
    lang === 'en'
      ? '\u26A0  This document was created with switchr.cc \u00b7 Editing in draw.io is not supported. Any changes made here may cause inconsistencies when the file is re-imported into SwitchR.'
      : '\u26A0  Dieses Dokument wurde mit switchr.cc erzeugt \u00b7 Eine Bearbeitung in draw.io wird nicht unterst\u00fctzt. \u00c4nderungen, die hier vorgenommen werden, k\u00f6nnen beim Reimport in SwitchR zu Inkonsistenzen f\u00fchren.';
  const w = SHEET.w - 2 * FRAME_OUTER;
  const style =
    'rounded=1;arcSize=4;html=1;fillColor=#FEF3C7;strokeColor=#B45309;strokeWidth=1.5;align=center;verticalAlign=middle;fontSize=13;fontStyle=1;fontColor=#7C2D12;whiteSpace=wrap;spacingLeft=12;spacingRight=12;';
  return (
    `<object id="${esc(id)}" netappNote="1" label="${esc(msg)}">` +
    `<mxCell style="${style}" vertex="1" parent="1"><mxGeometry x="${FRAME_OUTER}" y="-58" width="${w}" height="40" as="geometry"/></mxCell>` +
    `</object>`
  );
}

function devicePageDiagram(page, doc, lang, includeMeta, pageInfo, idgen) {
  const scale = page.contentScale || 1;
  let cells = frameCells(doc, lang, pageInfo, pageDisplayName(page.name, lang), usedConnTypes(doc), idgen, '');
  const nodeIndex = {};
  (page.nodes || []).forEach((n) => (nodeIndex[n.id] = n));
  // Map alias placement ids onto their master node so edge port labels resolve
  // for connections drawn on a clone's page.
  (page.nodes || []).forEach((n) => {
    if (!n.data?.aliasOf) return;
    for (const p of doc.pages || []) {
      const m = (p.nodes || []).find((x) => x.id === n.data.aliasOf && !x.data?.aliasOf);
      if (m) {
        nodeIndex[n.id] = m;
        break;
      }
    }
  });
  (page.nodes || []).forEach((n) => (cells += nodeCell(n, doc, lang, page.labelPos, scale)));
  const lagNameById = {};
  (doc.pages || []).forEach((p) => (p.lags || []).forEach((l) => (lagNameById[l.id] = l.name)));
  // Connections are ONE global topology: emit every edge whose both endpoints
  // are present on this page (master or clone). Edges whose home page differs
  // are emitted as MIRROR cells (netappMirror) — draw.io shows them, but the
  // importer skips them so the connection is never duplicated on round-trip.
  const dispEdges = displayEdgesForPage(doc, page);
  const bundles = computeSideBundles(dispEdges);
  dispEdges.forEach((e) => (cells += edgeCell(e, nodeIndex, lagNameById, !!e.__mirrored, page.id, bundles[e.id] || {})));
  if (includeMeta) cells += metaCell(doc);
  cells += noteCell(lang, idgen);
  return `<diagram id="${esc(page.id)}" name="${esc(pageDisplayName(page.name, lang))}">${modelWrap(cells)}</diagram>`;
}

// Detail pages are exported as native, editable cells (device boxes / panels +
// port rows + connection edges). The editable detail-page metadata (members,
// showStubs, viewMode) is also stored in the meta cell so the app can rebuild
// the live, interactive detail page on import (the native cells are skipped on
// import because the page's frame group carries netappDetail).
function detailDiagram(page, doc, lang, pageInfo, idgen) {
  const cells = detailCells(doc, page, lang, pageInfo, idgen) + noteCell(lang, idgen);
  return `<diagram id="${esc(page.id)}" name="${esc(pageDisplayName(page.name, lang))}">${modelWrap(cells)}</diagram>`;
}

// Location plans export as native cells (background image + markers +
// connections). Like detail pages, the editable page metadata (markers,
// background, showConnections) lives in the meta cell and the page is rebuilt
// from there on import (native cells are skipped via netappLocation).
function locationDiagram(page, doc, lang, pageInfo, idgen) {
  const cells = locationCells(doc, page, lang, pageInfo, idgen) + noteCell(lang, idgen);
  return `<diagram id="${esc(page.id)}" name="${esc(pageDisplayName(page.name, lang))}">${modelWrap(cells)}</diagram>`;
}

function autoDiagram(id, name, cells) {
  return `<diagram id="${id}" name="${esc(name)}">${modelWrap(cells)}</diagram>`;
}

export function exportDrawio(rawDoc, lang = 'de') {
  // Colors / image URIs end up as raw XML attribute + mxGraph style tokens:
  // sanitize once up-front so a corrupted document can never yield invalid XML.
  const doc = sanitizeDocImages(rawDoc);
  const idgen = makeIdGen();
  const pages = doc.pages || [];
  const total = pages.length + 2;
  const diagrams = [];
  let metaAttached = false;
  pages.forEach((p, i) => {
    const pageInfo = { index: i + 1, total };
    if (p.type === 'detail') {
      diagrams.push(detailDiagram(p, doc, lang, pageInfo, idgen));
    } else if (p.type === 'location') {
      diagrams.push(locationDiagram(p, doc, lang, pageInfo, idgen));
    } else {
      diagrams.push(devicePageDiagram(p, doc, lang, !metaAttached, pageInfo, idgen));
      metaAttached = true;
    }
  });
  if (!metaAttached) {
    diagrams.unshift(`<diagram id="netapp-meta-page" name="Meta">${modelWrap(metaCell(doc))}</diagram>`);
  }
  diagrams.push(autoDiagram('auto-cablelist', lang === 'en' ? 'Cable list' : 'Kabelliste', cableListCells(doc, lang, { index: total - 1, total }, idgen) + noteCell(lang, idgen)));
  diagrams.push(autoDiagram('auto-vlanlist', lang === 'en' ? 'VLAN list' : 'VLAN-Liste', vlanListCells(doc, lang, { index: total, total }, idgen) + noteCell(lang, idgen)));
  return `<mxfile host="netdraw" type="device" version="24.0.0">${diagrams.join('')}</mxfile>`;
}

// ---- Import ---------------------------------------------------------------
function stripHtml(s) {
  if (!s) return '';
  const txt = String(s)
    .replace(/<br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return txt.split(/\s{2,}|\u00b7/)[0].trim();
}

function guessDeviceTypeFromStyle(style) {
  const s = (style || '').toLowerCase();
  const known = ['switch', 'firewall', 'router', 'server', 'printer', 'modem', 'storage', 'cloud'];
  for (const k of known) if (s.includes('networks.' + k) || s.includes('.' + k)) return k === 'cloud' ? 'cloud' : k;
  if (s.includes('wireless') || s.includes('access')) return 'accesspoint';
  return 'switch';
}

function parseDrawioInternal(xmlString) {
  const parser = new DOMParser();
  const xdoc = parser.parseFromString(xmlString, 'text/xml');
  if (xdoc.querySelector('parsererror')) throw new Error('Invalid XML');
  if (!xdoc.querySelector('mxfile') && !xdoc.querySelector('mxGraphModel')) throw new Error('Not a draw.io file');

  // Tracks elements that could not be read back into the app's model and were
  // therefore dropped on import (shown to the user as an info message).
  const report = { droppedShapes: 0, droppedEdges: 0 };

  // global meta (vlans + title block)
  let vlans = null;
  let titleBlock = null;
  let name = null;
  let labelPosMap = {};
  let pageScaleMap = {};
  let zones = [];
  let zonesEnabled = false;
  const metaEl = xdoc.querySelector('[netappVlans]');
  if (metaEl) {
    try {
      vlans = JSON.parse(metaEl.getAttribute('netappVlans'));
    } catch {}
    try {
      titleBlock = JSON.parse(metaEl.getAttribute('netappTitleBlock'));
    } catch {}
    try {
      const lp = JSON.parse(metaEl.getAttribute('netappLabelPos') || '{}');
      if (lp && typeof lp === 'object') labelPosMap = lp;
    } catch {}
    try {
      const ps = JSON.parse(metaEl.getAttribute('netappPageScale') || '{}');
      if (ps && typeof ps === 'object') pageScaleMap = ps;
    } catch {}
    try {
      const zs = JSON.parse(metaEl.getAttribute('netappZones') || '[]');
      if (Array.isArray(zs)) zones = zs;
    } catch {}
    zonesEnabled = metaEl.getAttribute('netappZonesEnabled') === '1';
    name = metaEl.getAttribute('netappName') || null;
  }

  const diagrams = Array.from(xdoc.querySelectorAll('diagram'));
  const pages = [];

  const handleModel = (model, pageId, pageName) => {
    if (!model) return;
    if (model.querySelector('[netappAuto]')) return; // skip generated pages
    if (model.querySelector('[netappDetail]')) return; // detail pages are rebuilt from meta
    if (model.querySelector('[netappLocation]')) return; // location pages rebuilt from meta
    const root = model.querySelector('root');
    if (!root) return;
    const scale = Number(pageScaleMap[pageId]) || 1;
    const nodes = [];
    const edges = [];

    // Pre-index every cell's parent + local geometry so we can resolve a cell's
    // ABSOLUTE position even when draw.io reparented it into a group (e.g. the
    // full-sheet CAD frame) after the user moved it on the canvas. Without this
    // a moved device would change parent away from "1" and be dropped.
    const cellInfo = {};
    Array.from(root.children).forEach((ch) => {
      const isObj = ch.tagName === 'object' || ch.tagName === 'UserObject';
      const mc = isObj ? ch.querySelector('mxCell') : ch.tagName === 'mxCell' ? ch : null;
      if (!mc) return;
      const cid = (isObj ? ch.getAttribute('id') : mc.getAttribute('id')) || '';
      if (!cid) return;
      const g = mc.querySelector('mxGeometry');
      cellInfo[cid] = {
        parent: mc.getAttribute('parent') || '',
        x: parseFloat(g?.getAttribute('x') || '0'),
        y: parseFloat(g?.getAttribute('y') || '0'),
      };
    });
    const absPos = (cid) => {
      let x = 0;
      let y = 0;
      let cur = cid;
      let guard = 0;
      while (cur && cur !== '0' && cur !== '1' && cellInfo[cur] && guard++ < 50) {
        x += cellInfo[cur].x;
        y += cellInfo[cur].y;
        cur = cellInfo[cur].parent;
      }
      return { x, y };
    };

    Array.from(root.children).forEach((ch) => {
      const isObject = ch.tagName === 'object' || ch.tagName === 'UserObject';
      const wrapper = ch;
      const cell = isObject ? ch.querySelector('mxCell') : ch.tagName === 'mxCell' ? ch : null;
      if (!cell) return;
      if (wrapper.hasAttribute && wrapper.hasAttribute('netappMeta')) return;
      // Skip the SwitchR hint banner (netappNote) — visible only in draw.io.
      if (wrapper.hasAttribute && wrapper.hasAttribute('netappNote')) return;
      // Skip the CAD frame group (border / title block / legend). Its children
      // use parent != "1" and are already ignored; only the group object itself
      // is top-level, so skip it here so it is never counted as a dropped shape.
      if (wrapper.hasAttribute && wrapper.hasAttribute('netappFrame')) return;
      const id = (isObject ? wrapper.getAttribute('id') : cell.getAttribute('id')) || uid();
      if (cell.getAttribute('vertex') === '1') {
        // Alias placement (clone of a master device on another page): rebuild it
        // as a lightweight { aliasOf } node with its absolute position. Handled
        // BEFORE the device path because the cell also carries deviceType.
        if (isObject && wrapper.hasAttribute('netappAlias')) {
          const aliasOf = wrapper.getAttribute('netappAliasOf') || '';
          if (aliasOf) {
            const abs = absPos(id);
            nodes.push({ id, type: 'device', position: { x: Math.round(abs.x / scale), y: Math.round(abs.y / scale) }, data: { aliasOf } });
          } else {
            report.droppedShapes++;
          }
          return;
        }
        const isDevice = isObject && (wrapper.hasAttribute('netappNode') || wrapper.hasAttribute('deviceType'));
        const isTopLevel = cell.getAttribute('parent') === '1';
        // Non-top-level cells are normally frame children / edge labels and are
        // ignored silently. EXCEPTION: genuine SwitchR devices that draw.io
        // reparented into the CAD frame group when the user moved them — those
        // are still imported (with their absolute position) so they never vanish.
        if (!isTopLevel && !isDevice) return;
        const abs = absPos(id);
        const gx = abs.x;
        const gy = abs.y;
        if (gx <= -1500 && gy <= -1500) return; // hidden meta-ish
        // Only genuine SwitchR devices are read back; generic shapes added in
        // draw.io can't be mapped to the app model and are dropped (with notice).
        if (!isDevice) {
          report.droppedShapes++;
          return;
        }
        const deviceType =
          (isObject && wrapper.getAttribute('deviceType')) || guessDeviceTypeFromStyle(cell.getAttribute('style'));
        const label = (isObject && wrapper.getAttribute('netappLabel')) || stripHtml((isObject && wrapper.getAttribute('label')) || cell.getAttribute('value') || '');
        const ip = (isObject && wrapper.getAttribute('deviceIp')) || '';
        const description = (isObject && wrapper.getAttribute('netappDescription')) || '';
        let vlanRef = (isObject && wrapper.getAttribute('netappVlanRef')) || null;
        const vlanIdAttr = isObject && wrapper.getAttribute('vlanId');
        if (!vlanRef && vlanIdAttr && vlans) {
          const v = vlans.find((vv) => String(vv.vlanId) === String(vlanIdAttr));
          if (v) vlanRef = v.id;
        }
        let ports = [];
        const portsAttr = isObject && wrapper.getAttribute('netappPorts');
        if (portsAttr) {
          try {
            const parsed = JSON.parse(portsAttr);
            if (Array.isArray(parsed))
              ports = parsed
                .filter((p) => p && p.id)
                .map((p) => ({
                  id: p.id,
                  name: p.name || '',
                  ip: p.ip || '',
                  untaggedVlanRef: p.untaggedVlanRef || null,
                  taggedVlanRefs: Array.isArray(p.taggedVlanRefs) ? p.taggedVlanRefs.filter(Boolean) : [],
                }));
          } catch {}
        }
        let panel = { groups: [] };
        const panelAttr = isObject && wrapper.getAttribute('netappPanel');
        if (panelAttr) {
          try {
            const parsed = JSON.parse(panelAttr);
            if (parsed && Array.isArray(parsed.groups)) {
              panel = { groups: parsed.groups.filter((g) => g && typeof g.prefix === 'string').map((g) => ({ prefix: g.prefix, layout: g.layout || 'single' })) };
            }
          } catch {}
        }
        let portGroups = [];
        const pgAttr = isObject && wrapper.getAttribute('netappPortGroups');
        if (pgAttr) {
          try {
            const parsed = JSON.parse(pgAttr);
            if (Array.isArray(parsed))
              portGroups = parsed
                .filter((g) => g && g.id && Array.isArray(g.memberIds))
                .map((g) => ({
                  id: g.id,
                  name: g.name || '',
                  untaggedVlanRef: g.untaggedVlanRef || null,
                  taggedVlanRefs: Array.isArray(g.taggedVlanRefs) ? g.taggedVlanRefs.filter(Boolean) : [],
                  memberIds: g.memberIds.filter(Boolean),
                }));
          } catch {}
        }
        let interfaces = [];
        const ifAttr = isObject && wrapper.getAttribute('netappInterfaces');
        if (ifAttr) {
          try {
            const parsed = JSON.parse(ifAttr);
            if (Array.isArray(parsed))
              interfaces = parsed
                .filter((it) => it && it.id)
                .map((it) => ({
                  id: it.id,
                  name: it.name || '',
                  ip: it.ip || '',
                  untaggedVlanRef: it.untaggedVlanRef || null,
                  taggedVlanRefs: Array.isArray(it.taggedVlanRefs) ? it.taggedVlanRefs.filter(Boolean) : [],
                  taggedIps: it.taggedIps && typeof it.taggedIps === 'object' ? it.taggedIps : {},
                }));
          } catch {}
        }
        nodes.push({ id, type: 'device', position: { x: Math.round(gx / scale), y: Math.round(gy / scale) }, data: { deviceType, label, ip, description, vlanRef: vlanRef || null, ports, panel, portGroups, interfaces } });
      } else if (cell.getAttribute('edge') === '1') {
        // Mirror cells are display-only copies of a connection from another
        // page — skip them so the real edge is imported exactly once.
        if (isObject && wrapper.hasAttribute('netappMirror')) return;
        const source = cell.getAttribute('source');
        const target = cell.getAttribute('target');
        if (!source || !target) return;
        const connTypeAttr = (isObject && wrapper.getAttribute('netappConnType')) || 'ethernet';
        const sourcePortId = (isObject && wrapper.getAttribute('netappSourcePortId')) || null;
        const targetPortId = (isObject && wrapper.getAttribute('netappTargetPortId')) || null;
        const sourceSide = (isObject && wrapper.getAttribute('netappSourceSide')) || '';
        const targetSide = (isObject && wrapper.getAttribute('netappTargetSide')) || '';
        const lagId = (isObject && wrapper.getAttribute('netappLagId')) || '';
        const lagName = (isObject && wrapper.getAttribute('netappLagName')) || '';
        const cname = (isObject && (wrapper.getAttribute('netappName') || wrapper.getAttribute('label'))) || '';
        edges.push({
          id,
          source,
          target,
          name: cname || '',
          connType: connTypeAttr,
          sourcePortId: sourcePortId || null,
          targetPortId: targetPortId || null,
          sourceSide: sourceSide || undefined,
          targetSide: targetSide || undefined,
          lagId: lagId || null,
          _lagName: lagName,
        });
      }
    });
    // Drop dangling edges that reference shapes which were not imported.
    const nodeIds = new Set(nodes.map((n) => n.id));
    const validEdges = edges.filter((e) => {
      const ok = nodeIds.has(e.source) && nodeIds.has(e.target);
      if (!ok) report.droppedEdges++;
      return ok;
    });
    // Reconstruct LAG groups from the per-edge lagId/lagName attributes.
    const lagsMap = {};
    validEdges.forEach((e) => {
      if (e.lagId) {
        if (!lagsMap[e.lagId]) lagsMap[e.lagId] = { id: e.lagId, name: e._lagName || 'LAG', color: '#0B5FFF', memberEdgeIds: [] };
        lagsMap[e.lagId].memberEdgeIds.push(e.id);
      }
      delete e._lagName;
    });
    const lags = Object.values(lagsMap);
    pages.push({ id: pageId || uid(), name: pageName || 'Seite', type: 'standard', nodes, edges: validEdges, lags, labelPos: labelPosMap[pageId] === 'left' ? 'left' : 'below', contentScale: scale });
  };

  if (diagrams.length) {
    diagrams.forEach((dg, i) => {
      const model = dg.querySelector('mxGraphModel');
      handleModel(model, dg.getAttribute('id') || uid(), dg.getAttribute('name') || 'Seite ' + (i + 1));
    });
  } else {
    handleModel(xdoc.querySelector('mxGraphModel'), uid(), 'Seite 1');
  }

  // Rebuild detail pages from meta
  let detailMeta = [];
  let pageOrder = null;
  if (metaEl) {
    try {
      detailMeta = JSON.parse(metaEl.getAttribute('netappDetailPages') || '[]') || [];
    } catch {}
    try {
      pageOrder = JSON.parse(metaEl.getAttribute('netappPageOrder') || 'null');
    } catch {}
  }
  const detailPages = (Array.isArray(detailMeta) ? detailMeta : []).map((d) => ({
    id: d.id || uid(),
    name: d.name || 'Detail',
    type: 'detail',
    members: Array.isArray(d.members)
      ? d.members.filter((m) => m && m.deviceId).map((m) => ({ deviceId: m.deviceId, position: m.position || { x: 80, y: 80 } }))
      : [],
    showStubs: !!d.showStubs,
    viewMode: d.viewMode === 'panel' ? 'panel' : 'list',
    contentScale: Number(d.contentScale) || 1,
  }));

  // Rebuild location plans from meta
  let locationMeta = [];
  if (metaEl) {
    try {
      locationMeta = JSON.parse(metaEl.getAttribute('netappLocationPages') || '[]') || [];
    } catch {}
  }
  const locationPagesRebuilt = (Array.isArray(locationMeta) ? locationMeta : []).map((l) => ({
    id: l.id || uid(),
    name: l.name || 'Positionsplan',
    type: 'location',
    markers: Array.isArray(l.markers)
      ? l.markers.filter((m) => m && m.deviceId).map((m) => ({ deviceId: m.deviceId, position: m.position || { x: 80, y: 80 } }))
      : [],
    background:
      l.background && typeof l.background === 'object'
        ? { kind: l.background.kind || 'none', dataUri: safeImageHref(l.background.dataUri) }
        : { kind: 'none', dataUri: '' },
    showConnections: l.showConnections !== false,
  }));

  let allPages = [...pages, ...detailPages, ...locationPagesRebuilt];

  // Restore original page order (standard + detail interspersed)
  if (Array.isArray(pageOrder) && pageOrder.length) {
    const byId = {};
    allPages.forEach((p) => (byId[p.id] = p));
    const ordered = [];
    pageOrder.forEach((pid) => {
      if (byId[pid]) {
        ordered.push(byId[pid]);
        delete byId[pid];
      }
    });
    allPages.forEach((p) => {
      if (byId[p.id]) ordered.push(p);
    });
    allPages = ordered;
  }

  const finalPages = allPages.length ? allPages : [{ id: uid(), name: 'Seite 1', type: 'standard', nodes: [], edges: [], labelPos: 'below' }];

  const doc = {
    name: name || 'Importiertes Projekt',
    zonesEnabled,
    zones,
    vlans: vlans || [],
    pages: finalPages,
    titleBlock: titleBlock && typeof titleBlock === 'object' ? { ...defaultTitleBlock(), ...titleBlock } : defaultTitleBlock(),
  };
  // Untrusted input: logo / background URIs must be plain image sources only.
  return { doc: sanitizeDocImages(doc), report };
}

// Returns just the document (used by tests and internal callers).
export function parseDrawio(xmlString) {
  return parseDrawioInternal(xmlString).doc;
}

// Returns the document plus an import report describing any dropped elements,
// so the UI can inform the user that the round-trip was lossy.
export function parseDrawioWithReport(xmlString) {
  return parseDrawioInternal(xmlString);
}
